import React, { useEffect, useMemo, useState } from "react";
import RawDataTable from "react-data-table-component";
import {
  Checkbox,
  MenuItem,
  ListItemText,
  IconButton,
  Menu,
  Tooltip,
  Popover,
} from "@material-ui/core";
import ViewColumnIcon from "@material-ui/icons/ViewColumn";
import SearchIcon from "@material-ui/icons/Search";
import { Heading, Select, Input } from ".";
import { timestampRenderer, timestampMsRenderer } from "../utils/helpers";
import { useLocalStorage } from "../utils/localstorage";

import _ from "lodash";
import { FilterList } from "@material-ui/icons";
import ReplayIcon from "@material-ui/icons/Replay";
import AddIcon from "@material-ui/icons/Add";
import { NavLink, Button } from ".";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { TextField, InputAdornment } from "@material-ui/core";
import FormControl from "@material-ui/core/FormControl";
import { colors } from "../theme/variables";
import CloseIcon from "@material-ui/icons/Close";
import CopyableCell from "../pages/CopyableCell";
import { ResizableHeaderCell } from "./ResizableHeaderCell";

export const DEFAULT_ROWS_PER_PAGE = 15;

export default function DataTableWfSearch(props) {
  const {
    localStorageKey,
    columns,
    data,
    options,
    defaultShowColumns,
    paginationPerPage = 15,
    showFilter = true,
    showColumnSelector = true,
    showRefresh = false,
    handleRefreshClick,
    customBtn,
    paginationServer = false,
    executionFilter = false,
    handleExecutionFilterClick,
    title,
    onFilterChange,
    initialFilterObj,
    ...rest
  } = props;

  // If no defaultColumns passed - use all columns
  const defaultColumns = useMemo(
    () =>
      props.defaultShowColumns || props.columns.map((col) => getColumnId(col)),
    [props.defaultShowColumns, props.columns]
  );

  const [tableState, setTableState] = useLocalStorage(
    localStorageKey,
    defaultColumns
  );

  // Add state for column widths
  const [columnWidths, setColumnWidths] = useState(() => {
    // Initialize with existing width values from columns
    const widths = {};
    columns.forEach(col => {
      widths[getColumnId(col)] = col.width || '200px';
    });
    return widths;
  });

  // Handler for resizing columns
  const handleResize = (columnId) => (e, { size }) => {
    setColumnWidths(prev => ({
      ...prev,
      [columnId]: `${size.width}px`
    }));
  };

  const handleFilterChange = (val) => {
    setFilterObj(val);
    if (onFilterChange) {
      if (!_.isEmpty(val.substring)) {
        onFilterChange(val);
      } else {
        onFilterChange(undefined);
      }
    }
  };

// Append bodyRenderer for date fields;
const dataTableColumns = useMemo(() => {
  let viewColumns = [];
  if (tableState) {
    for (let col of columns) {
      if (tableState.includes(getColumnId(col))) {
        viewColumns.push(col);
      }
    }
  } else {
    viewColumns = columns;
  }

  return viewColumns.map((column) => {
    let {
      id,
      name,
      label,
      type,
      renderer,
      wrap = true,
      sortable = true,
      ...rest
    } = column;

    const columnId = getColumnId(column);
    const width = columnWidths[columnId] || '200px';

    const internalOptions = {};
    if (type === "date") {
      internalOptions.format = (row) =>
        timestampRenderer(_.get(row, name));
    } else if (type === "date-ms") {
      internalOptions.format = (row) =>
        timestampMsRenderer(_.get(row, name));
    } else if (type === "json") {
      internalOptions.format = (row) =>
        JSON.stringify(_.get(row, name));
    }

    if (renderer) {
      // Wrap the output of the provided renderer
      internalOptions.format = (row) => {
        const cellContent = renderer(_.get(row, name), row);
        // Assuming cellContent is string or a node displaying the value
        const value =
          typeof cellContent === "string"
            ? cellContent
            : _.get(row, name)?.toString() || "";
        return <CopyableCell text={value}>{cellContent}</CopyableCell>;
      };
    } else {
      // Otherwise, use the raw value
      internalOptions.format = (row) => {
        const cellValue = _.get(row, name);
        const text = cellValue !== undefined && cellValue !== null
          ? cellValue.toString()
          : "";
        return <CopyableCell text={text}>{text}</CopyableCell>;
      };
    }

    // Wrap the column name in resizable header
    const resizableColumnName = (
      <ResizableHeaderCell
        onResize={handleResize(columnId)}
        width={width}
      >
        {getColumnLabel(column)}
      </ResizableHeaderCell>
    );

    return {
      id: columnId,
      selector: name,
      name: resizableColumnName,
      sortable: sortable,
      width: width, // Use the stored width
      maxWidth: "none", // Remove the maxWidth constraint to allow resizing
      wrap: wrap,
      type,
      ...internalOptions,
      ...rest,
    };
  });
}, [tableState, columns, columnWidths]); // Add columnWidths to dependencies

  const DEFAULT_FILTER_OBJ = {
    columnName: "",
    substring: "",
  };

  const [filterObj, setFilterObj] = useState(
    initialFilterObj || DEFAULT_FILTER_OBJ
  );

  const filteredItems = useMemo(() => {
    const column = dataTableColumns.find(
      (col) => col.id === filterObj.columnName
    );

    if (!filterObj.substring || !filterObj.columnName) {
      return data;
    } else {
      try {
        const regexp = new RegExp(filterObj.substring, "i");

        return data.filter((row) => {
          let target;
          if (
            column.type === "json" ||
            column.type === "date" ||
            column.type === "date-ms" ||
            column.searchable === "calculated"
          ) {
            target = column.format(row);

            if (!_.isString(target)) {
              target = JSON.stringify(target);
            }
          } else {
            target = _.get(row, column.selector);
          }

          return _.isString(target) && regexp.test(target);
        });
      } catch (e) {
        // Bad or incomplete Regexp
        console.log(e);
        return [];
      }
    }
  }, [data, dataTableColumns, filterObj]);

  return (
    <RawDataTable
      // title={<Heading level={0}>{title}</Heading>}
      title=""
      columns={dataTableColumns}
      data={filteredItems}
      pagination
      paginationServer={paginationServer}
      paginationPerPage={paginationPerPage}
      paginationRowsPerPageOptions={[15, 30, 50, 100, 500]}
      actions={
        <>
          {executionFilter && (
            <Tooltip title="Show Filters">
              <IconButton
                onClick={handleExecutionFilterClick}
                //  label="Columns"
                color={"primary"}
                className="raised-btn">
                <FilterList />
              </IconButton>
            </Tooltip>
          )}
          {!paginationServer && showFilter && (
            <Filter
              columns={dataTableColumns}
              filterObj={filterObj}
              setFilterObj={handleFilterChange}
            />
          )}
          {showColumnSelector && (
            <ColumnsSelector
              columns={columns}
              selected={tableState}
              setSelected={setTableState}
              defaultColumns={defaultColumns}
            />
          )}
          {showRefresh && (
            <Tooltip title="Refresh">
              <IconButton
                onClick={() => {
                  handleRefreshClick();
                }}
                color="primary"
                label="Refresh"
                className="raised-btn">
                <ReplayIcon />
              </IconButton>
            </Tooltip>
          )}

          {customBtn && (
            <div className={customBtn.className}>
              <Button
                component={NavLink}
                path={customBtn.link}
                startIcon={<AddIcon />}
                style={{
                  color: colors.wolkenTextColor,
                  margin: "0.5rem 0.3rem",
                  padding: "0.5rem",
                }}>
                {customBtn.btnText}
              </Button>
            </div>
          )}
        </>
      }
      {...rest}
    />
  );
}

function Filter({ columns, filterObj, setFilterObj }) {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const displayCol = columns
    .filter(
      (col) =>
        ![
          "createTime",
          "version",
          "schemaVersion",
          "restartable",
          "workflowStatusListenerEnabled",
          "inputParameters",
          "outputParameters",
          "task_types",
          "task_count",
          "actions",
          "executions_link",
          "timeoutSeconds",
        ].includes(col.id)
    )
    .map((col) => {
      let name = getColumnLabel(col);
      if (typeof name === "object") {
        name = name.props?.title ? name.props.title : getColumnId(col);
      }
      return {
        ...col,
        label: name,
        id: getColumnId(col),
      };
    });

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleValueChange = (v) => {
    setFilterObj({
      columnName: filterObj.columnName,
      substring: v,
    });
  };

  const handleColumnChange = (e, v) => {
    setFilterObj({
      columnName: v?.id ? v.id : "",
      substring: "",
    });
  };

  return (
    <>
      <Tooltip title="Filter">
        <IconButton
          onClick={handleClick}
          label="Columns"
          color={"primary"}
          className="raised-btn">
          <SearchIcon />
        </IconButton>
      </Tooltip>
      <Popover
        onClose={handleClose}
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        anchorOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "center",
          horizontal: "right",
        }}
        PaperProps={{
          style: { padding: 10, display: "flex", flexDirection: "row" },
        }}>
        <FormControl>
          <Autocomplete
            disableClearable
            id="fields"
            style={{ marginRight: 15, marginTop: 4, width: 200 }}
            options={displayCol}
            getOptionLabel={(option) => option.label}
            onChange={(e, v) => handleColumnChange(e, v)}
            getOptionSelected={(option, value) => option.id === value.id}
            value={
              filterObj.columnName
                ? displayCol.find((col) => col.id === filterObj.columnName)
                : null
            }
            renderInput={(params) => (
              <TextField
                {...params}
                autoComplete="off"
                label="Field"
                variant="outlined"
                ref={params.InputProps.ref}
                InputProps={{
                  ...params.InputParams,
                  endAdornment: filterObj.columnName && (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => {
                          handleColumnChange(null, "");
                        }}
                        size="small">
                        <CloseIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />
        </FormControl>
        <Input
          clearable
          label="Substring"
          style={{ marginRight: 15, marginTop: 4, width: 200 }}
          value={filterObj.substring}
          onChange={handleValueChange}
          disabled={filterObj.columnName === ""}
        />
      </Popover>
    </>
  );
}

function getColumnLabelById(columnId, columns) {
  const col = columns.find((c) => c.id === columnId || c.name === columnId);
  return col ? col.label || col.name : "";
}

function getColumnLabel(col) {
  return col.label || col.name;
}

function getColumnId(col) {
  return col.id || col.name;
}

function ColumnsSelector({ columns, selected, setSelected, defaultColumns }) {
  const [anchorEl, setAnchorEl] = React.useState(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleChange = (columnId, checked) => {
    if (!checked && selected.includes(columnId)) {
      setSelected(selected.filter((v) => v !== columnId));
    } else {
      setSelected([...selected, columnId]);
    }
  };

  const reset = () => {
    setSelected(defaultColumns);
  };
  return (
    <>
      <Tooltip title="Show Columns">
        <IconButton
          onClick={handleClick}
          label="Columns"
          color="primary"
          className="raised-btn">
          <ViewColumnIcon />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        getContentAnchorEl={null}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}>
        {[
          ...columns.map((column) => (
            <MenuItem key={getColumnId(column)} value={getColumnId(column)}>
              <Checkbox
                checked={selected.includes(getColumnId(column))}
                color="primary"
                onChange={(e) =>
                  handleChange(getColumnId(column), e.target.checked)
                }
              />
              <ListItemText primary={getColumnLabel(column)} />
            </MenuItem>
          )),
          <MenuItem key="_reset" value="_reset" onClick={reset}>
            <ListItemText>Reset to default</ListItemText>
          </MenuItem>,
        ]}
      </Menu>
    </>
  );
}
